import React, { ForwardedRef, forwardRef, memo, useEffect, useReducer, useState } from "react";
import Stack from "@mui/material/Stack";
import {
  EDDataGrid,
  EDIcon,
  EDIconSize,
  EDTextField,
  EDTypography,
  grey,
  primary,
  spacing,
  success
} from "@trailblazer-game/ed-webkit";
import { styled } from "@mui/material/styles";
import { theme } from "../../../Theme/Theme";
import { getTimestampDateFormatted } from "../../../../helpers/dates";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ILeagueRank } from "../../../../helpers/interfaces/league";
import ReplayIcon from '@mui/icons-material/Replay';
import {
  ELeagueLeaderboardActions,
  ELeagueRankUidField,
  ELeagueRankSearchField,
  leagueLeaderboardInitialState,
  leagueLeaderboardReducer
} from "./League_LeaderboardState";
import { IWithLazyLoadProps, withLazyLoad } from "../../../../hooks/withLazyLoad";
import MoreVertIcon from '@mui/icons-material/MoreVert';

interface ILeagueLeaderboardProps extends IWithLazyLoadProps {
  className?: string;
  reloadRanks: Function;
  ranks: ILeagueRank[];
  columns: any[],
  uidField: ELeagueRankUidField,
  uid: string | null,
  searchPlaceholder: string,
  searchField: ELeagueRankSearchField
}

const ELEMENTS_PER_PAGE = 10;
const REFRESH_TIMEOUT = 60 * 1000;

const LeagueLeaderboard = memo(forwardRef(({
                                             className,
                                             reloadRanks,
                                             ranks,
                                             uidField,
                                             columns,
                                             searchPlaceholder,
                                             searchField,
                                             uid
                                           }: ILeagueLeaderboardProps, ref: ForwardedRef<HTMLDivElement>) => {
  const [ state, dispatch ] = useReducer(leagueLeaderboardReducer, leagueLeaderboardInitialState);
  const [ nameFilter, setNameFilter ] = useState("");
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));


  useEffect(() => {
    let dataInterval = setInterval(() => {
      reloadRanks();
    }, REFRESH_TIMEOUT);

    return () => clearInterval(dataInterval);
  }, []);

  useEffect(() => {
    dispatch({ type: ELeagueLeaderboardActions.SetRankings, ranks });
  }, [ ranks ]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch({ type: ELeagueLeaderboardActions.SetNameFilter, filter: nameFilter, filterField: searchField });
    }, 250);

    return () => clearTimeout(timeout);
  }, [ nameFilter ])

  if (!ranks) {
    return null;
  }

  const onPageChange = (page: number) => {
    dispatch({ type: ELeagueLeaderboardActions.SetPage, page });
  }

  const getUserRank = () => {
    if (state.ranks.length <= ELEMENTS_PER_PAGE) {
      return null;
    }

    if (state.ranks[state.ranks.length - 1][uidField] === uid) {
      return null;
    }

    // check the player position only if it's not the last one and not in the top 10
    return state.ranks.find((r) => r.rank > ELEMENTS_PER_PAGE && r[uidField] == uid);
  }

  const onSearch = (value: string) => {
    setNameFilter(value);
  }

  const userRank = getUserRank();

  return (
    <Stack className={className} spacing={4} alignItems={"flex-start"} ref={ref}>
      <Stack direction={isSmall ? "column" : "row"} spacing={2} alignItems={"center"}
             justifyContent={isSmall ? "center" : "flex-start"} sx={{ width: "100%" }}>
        <EDTypography variant={"h3"} sx={{ color: primary("main") }}>
          LEADERBOARD
        </EDTypography>
        <EDTypography variant={"caption"} sx={{ color: grey("500") }}>
          Last updated: {getTimestampDateFormatted(state.lastUpdated.getTime())}
        </EDTypography>
        <Stack direction={"row"} spacing={0.5} alignItems={"center"} justifyContent={"center"}
               onClick={() => reloadRanks()} sx={{ cursor: "pointer", color: grey("400") }}>
          <EDIcon icon={<ReplayIcon/>} size={EDIconSize.Small}/>
          <EDTypography variant={"caption"} sx={{ color: grey("500") }}>
            REFRESH
          </EDTypography>
        </Stack>
      </Stack>

      <EDTextField placeholder={searchPlaceholder} onChange={(value) => onSearch(value)}/>

      <Stack alignItems={"center"} justifyContent={"center"} spacing={1} sx={{ width: '100%' }}>

        {/* Current player rank in one row table, if user is not on the first page */}
        {userRank &&
            <>
                <EDDataGrid autoHeight={true} columns={columns} rows={[ userRank ]}
                            getRowId={(row) => row[uidField]}
                            disableColumnFilter={true}
                            disableColumnMenu={true}
                            disableSelectionOnClick={true}
                            disableHeader={false}
                            disablePagination={true}
                            getRowHeight={() => 'auto'}
                            sx={{
                              '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: spacing(0.5) },
                              '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: spacing(1) },
                              '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: spacing(2) },
                            }}
                            getRowClassName={() => 'player-row'}/>
                <EDIcon icon={<MoreVertIcon/>} sx={{ color: grey("600") }}/>
            </>
        }

        <EDDataGrid columns={columns}
                    autoHeight={true}
                    rows={state.ranks}
                    getRowId={(row) => row[uidField]}
                    disableColumnFilter={true}
                    disableSelectionOnClick={true}
                    disableColumnMenu={true}
                    rowsPerPageOptions={[ 10, 25, 50 ]}
                    onPageChange={onPageChange}
                    disableHeader={!!userRank}
                    initialState={{
                      pagination: {
                        pageSize: ELEMENTS_PER_PAGE
                      }
                    }}
                    page={state.activePage}
                    getRowHeight={() => 'auto'}
                    sx={{
                      '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: spacing(0.5) },
                      '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: spacing(1) },
                      '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: spacing(2) },
                    }}
                    getRowClassName={(params) => {
                      const isPlayer = params.row[uidField] == uid;
                      const oddOrEven = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
                      const isTop3 = params.row.rank <= 3;
                      return `${oddOrEven} ${isPlayer ? 'player-row' : ''} ${isTop3 ? "top-3" : ""}`;
                    }}/>
      </Stack>
    </Stack>
  );
}));

LeagueLeaderboard.displayName = "LeagueLeaderboard";

const LeagueLeaderboardStyled = styled(LeagueLeaderboard)(() => ({
  width: '100%',
  position: "relative",
  zIndex: 10,
  ".winner-icon": {
    position: "absolute",
    top: "-4px",
    left: "-8px",
    zIndex: 10,
    margin: '0 !important'
  },
  ".top-3": {
    background: "linear-gradient(90deg, #263639 0%, #3D3D3D 33.85%)"
  },
  "& .MuiDataGrid-root .MuiDataGrid-virtualScroller .MuiDataGrid-row.player-row": {
    backgroundColor: `${grey("300")} !important`,
    color: grey("1000"),
    "&.top-3": {
      background: `linear-gradient(90deg, #263639 0%, ${grey("200")} 33.85%)`
    }
  },
  ".top-3 .matches-wins-losses": {
    backgroundColor: grey("900")
  }
}));

// Export
export default withLazyLoad(LeagueLeaderboardStyled);
